import React, { ElementType, MouseEventHandler, ReactNode } from 'react'

import Box from 'components_new/atoms/Box'
import Text from 'components_new/atoms/Text'
import Dialog from 'components_new/atoms/Dialog'
import DialogTitle from 'components_new/atoms/Dialog/DialogTitle'

// These are going to be replaced with our own components later.
import {
  DialogContent,
  DialogActions,
  DialogContentText,
  SxProps
} from '@mui/material'
import IconButton from 'components_new/atoms/IconButton'
import Icon from 'components_new/atoms/Icon'

interface SimpleDialogProps {
  actions?: ReactNode
  children?: ReactNode
  component?: ElementType<any>
  contentHero?: {
    body?: ReactNode
    title: string
  }
  contentText?: ReactNode
  disableOverflow?: boolean
  disablePortal?: boolean
  disableRestoreFocus?: boolean
  dividers?: boolean
  fullHeight?: boolean
  fullWidth?: boolean
  hasClose?: boolean
  helperText?: ReactNode
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  onClose: () => void
  onMouseDown?: MouseEventHandler<HTMLElement>
  open: boolean
  PaperProps?: object
  sx?: object
  title?: ReactNode
  titleHero?: ReactNode
  titleHeroSx?: SxProps
}

/**
 * The SimpleDialog component is a
 * variant of a Dialog that is smaller
 * and simpler, and therefore suitable
 * for displaying short content (i.e.
 * a promt or a short form).
 */

const SimpleDialog = React.forwardRef((props: SimpleDialogProps, ref: any) => {
  const {
    actions,
    children,
    contentHero,
    contentText,
    disableOverflow,
    dividers,
    fullHeight,
    fullWidth = true,
    hasClose,
    helperText,
    maxWidth = 'sm',
    onMouseDown = (e) => e.stopPropagation(),
    title,
    titleHero,
    titleHeroSx,
    ...rest
  } = props

  return (
    <Dialog
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      onMouseDown={onMouseDown}
      {...rest}
      ref={ref}
    >
      {title ? (
        <DialogTitle
          sx={{
            pb: contentText ? 0.5 : undefined,
            pr: hasClose ? 6 : undefined
          }}
        >
          {title}
          {helperText ? (
            <Text
              color="text.secondary"
              component="div"
              sx={{ mt: '0.25em' }}
              variant="caption"
            >
              {helperText}
            </Text>
          ) : null}
        </DialogTitle>
      ) : null}
      {hasClose ? (
        <Box sx={{ p: 1, position: 'absolute', right: 0, top: 0 }}>
          <IconButton onClick={props.onClose}>
            <Icon name="Close" />
          </IconButton>
        </Box>
      ) : null}
      {titleHero ? (
        <Box sx={{ px: 3, py: 2, ...titleHeroSx }}>{titleHero}</Box>
      ) : null}
      <DialogContent
        dividers={dividers}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: fullHeight ? '90vh' : undefined,
          overflow: disableOverflow ? 'hidden' : undefined,
          position: 'relative',
          pt: contentText ? 0 : undefined
        }}
      >
        {contentHero ? (
          <Box
            sx={{
              bgcolor: 'brand.background',
              borderRadius: 1,
              mb: 3,
              p: 3
            }}
          >
            <Text
              color="text.primary"
              component="h1"
              gutterBottom={true}
              variant="h3"
            >
              {contentHero.title}
            </Text>
            {contentHero.body ? (
              <Text color="text.primary" component="div" variant="body1">
                {contentHero.body}
              </Text>
            ) : null}
          </Box>
        ) : null}
        {contentText ? (
          <DialogContentText sx={{ maxWidth: 'title', mb: 2 }}>
            {contentText}
          </DialogContentText>
        ) : null}
        {children}
      </DialogContent>
      {actions ? <DialogActions>{actions}</DialogActions> : null}
    </Dialog>
  )
})

SimpleDialog.displayName = 'SimpleDialog'
export default SimpleDialog
